import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { IoWalletOutline } from "react-icons/io5";
import { MdAdd, MdOutlineCampaign, MdOutlineChat } from "react-icons/md";
import PageLayout from "../../components/layouts/PageLayout";
import StatsSection from "./StatsSection";
import ActionPanel from "./ActionPanel";
import PerformanceChart from "./PerformanceChart";
import MessageStatusCard from "./MessageStatusCard";
import { AppDispatch, RootState } from "../../store";
import { getAuthUser, getDataUserId } from "../../util/manageAuth";
import { fetchWalletDetails } from "../../features/wallet/WalletServices";
import { fetchDashboardData } from "../../features/dashboard/DasboardServices";
import { DashboardData } from "../../types/dashboardType";

const greeting = () => {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
};

export default function DashboardPage() {
  const dispatch = useDispatch<AppDispatch>();
  const user = getAuthUser();
  const userId = getDataUserId();

  const { data: wallet, loading: walletLoading } = useSelector((state: RootState) => state.wallet);

  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (userId && !wallet) {
      dispatch(fetchWalletDetails(Number(userId)));
    }
  }, [dispatch, userId, wallet]);

  useEffect(() => {
    const loadDashboard = async () => {
      setLoading(true);
      const resultAction = await dispatch(fetchDashboardData());
      if (fetchDashboardData.fulfilled.match(resultAction)) {
        setDashboardData(resultAction.payload);
      }
      setLoading(false);
    };
    loadDashboard();
  }, [dispatch]);

  const firstName = String(user?.userName || "").split(" ")[0];
  const today = new Date().toLocaleDateString([], { weekday: "long", day: "numeric", month: "long" });

  return (
    <PageLayout>
      <div className="space-y-4 sm:space-y-5">
        {/* Welcome banner */}
        <section className="relative overflow-hidden rounded-3xl bg-[#0b1220] text-white p-6 sm:p-8 ring-1 ring-white/[0.06] shadow-[0_20px_40px_-24px_rgba(11,18,32,0.6)]">
          {/* Mesh glows */}
          <div className="pointer-events-none absolute -top-32 right-[8%] w-[420px] h-[420px] rounded-full bg-brand-main/40 blur-[90px]" />
          <div className="pointer-events-none absolute -bottom-40 right-[38%] w-[360px] h-[360px] rounded-full bg-emerald-500/20 blur-[90px]" />
          {/* Fine grid, fading out to the left */}
          <div
            className="pointer-events-none absolute inset-0 opacity-[0.07]"
            style={{
              backgroundImage:
                "linear-gradient(rgba(255,255,255,0.9) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.9) 1px, transparent 1px)",
              backgroundSize: "28px 28px",
              maskImage: "linear-gradient(to left, black 10%, transparent 75%)",
              WebkitMaskImage: "linear-gradient(to left, black 10%, transparent 75%)",
            }}
          />

          <div className="relative flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <div>
              <span className="inline-flex items-center gap-2 h-7 px-3 rounded-full bg-white/[0.07] ring-1 ring-inset ring-white/10 text-[11.5px] font-medium text-slate-300">
                <span className="relative flex w-1.5 h-1.5">
                  <span className="absolute inset-0 rounded-full bg-emerald-400 animate-ping opacity-60" />
                  <span className="relative w-1.5 h-1.5 rounded-full bg-emerald-400" />
                </span>
                {today}
              </span>
              <h1 className="mt-4 text-[26px] sm:text-[32px] leading-tight font-semibold tracking-[-0.02em]">
                {greeting()}
                {firstName ? `, ${firstName.charAt(0).toUpperCase()}${firstName.slice(1).toLowerCase()}` : ""}
              </h1>
              <p className="mt-2 text-[14.5px] text-slate-400 max-w-[520px]">
                Here's how your WhatsApp messaging is performing today.
              </p>
            </div>

            <div className="flex flex-wrap gap-2.5">
              <Link
                to="/admin/live-chat"
                className="inline-flex items-center gap-2 h-11 px-4 rounded-xl bg-white/[0.06] text-white text-[13.5px] font-medium ring-1 ring-inset ring-white/15 hover:bg-white/[0.12] transition"
              >
                <MdOutlineChat size={18} className="text-slate-300" />
                Open live chat
              </Link>
              <Link
                to="/admin/variables-send-message/bulk"
                className="inline-flex items-center gap-2 h-11 px-4 rounded-xl bg-white text-[#0b1220] text-[13.5px] font-semibold shadow-[0_1px_2px_rgba(0,0,0,0.2),inset_0_-1px_0_rgba(15,23,42,0.08)] hover:bg-slate-100 transition"
              >
                <MdOutlineCampaign size={18} />
                New campaign
              </Link>
            </div>
          </div>
        </section>

        {/* KPI cards */}
        <StatsSection data={dashboardData} loading={loading} />

        <div className="grid grid-cols-12 gap-4 sm:gap-5">
          {/* Left: chart + status */}
          <div className="col-span-12 xl:col-span-8 space-y-4 sm:space-y-5">
            <PerformanceChart />
            <MessageStatusCard data={dashboardData} loading={loading} />
          </div>

          {/* Right: wallet + quick actions */}
          <div className="col-span-12 xl:col-span-4 space-y-4 sm:space-y-5">
            <div className="bg-white rounded-3xl border border-app-line shadow-card p-5 sm:p-6">
              <div className="flex items-center justify-between">
                <h3 className="text-[15px] font-semibold text-app-ink">Wallet</h3>
                <span className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center">
                  <IoWalletOutline size={19} />
                </span>
              </div>

              <p className="text-[12.5px] text-app-muted mt-1">Available balance</p>
              {walletLoading && !wallet ? (
                <div className="mt-2 h-9 w-36 rounded-lg bg-app-bg animate-pulse" />
              ) : (
                <p className="mt-1 text-[30px] leading-10 font-bold tracking-tight text-app-ink tabular-nums">
                  ₹{" "}
                  {(wallet?.balance ?? 0).toLocaleString("en-IN", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </p>
              )}

              <Link
                to="/admin/wallet"
                className="mt-5 w-full inline-flex items-center justify-center gap-2 h-11 rounded-xl bg-[#0b1220] text-white text-[13.5px] font-semibold shadow-[inset_0_1px_0_rgba(255,255,255,0.1),0_1px_2px_rgba(11,18,32,0.3)] hover:bg-[#1a2336] transition"
              >
                <MdAdd size={18} />
                Manage wallet
              </Link>
            </div>

            <ActionPanel />
          </div>
        </div>
      </div>
    </PageLayout>
  );
}
